import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, CURRENCIES, currentMonth } from "@/lib/format";
import { Button, Input, Select, Card, Modal, PageHeader } from "@/components/Kit";
import { Plus, PencilSimple, Trash, ArrowUpRight, ArrowDownRight } from "@phosphor-icons/react";

const empty = (cur) => ({
  type: "EXPENSE", amount: "", currency: cur, categoryId: "", description: "", date: new Date().toISOString().slice(0, 10),
});

export default function Transactions() {
  const { user } = useAuth();
  const [txns, setTxns] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty(user?.baseCurrency || "USD"));
  const [saving, setSaving] = useState(false);

  const catMap = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);

  const load = async () => {
    const [t, c] = await Promise.all([api.get("/transactions"), api.get("/categories")]);
    setTxns(t.data);
    setCats(c.data);
  };
  useEffect(() => { load(); }, []);

  const filteredCats = cats.filter((c) => c.type === form.type);

  const openNew = () => {
    setEditing(null);
    setForm(empty(user?.baseCurrency || "USD"));
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ type: t.type, amount: String(t.amount), currency: t.currency, categoryId: t.categoryId || "", description: t.description || "", date: t.date });
    setOpen(true);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        amount: parseFloat(form.amount),
        currency: form.currency,
        categoryId: form.categoryId ? form.categoryId : null,
        description: form.description,
        date: form.date,
      };
      if (editing) await api.put(`/transactions/${editing.id}`, payload);
      else await api.post("/transactions", payload);
      toast.success(editing ? "Transaction updated" : "Transaction added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Transaction deleted");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <PageHeader title="Transactions" subtitle="Every income and expense, in one place.">
        <Button onClick={openNew} data-testid="add-transaction-btn"><Plus size={16} weight="bold" /> Add Transaction</Button>
      </PageHeader>

      <Card className="overflow-hidden">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-white/10 text-xs uppercase tracking-wider text-[#52525B]">
          <div className="col-span-5">Description</div>
          <div className="col-span-3">Category</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-2 text-right">Amount</div>
        </div>
        {txns.length === 0 ? (
          <p className="text-sm text-[#52525B] py-16 text-center">No transactions yet.</p>
        ) : (
          txns.map((t) => {
            const isIncome = t.type === "INCOME";
            const cat = catMap[t.categoryId];
            return (
              <div key={t.id} className="grid grid-cols-12 items-center px-6 py-3.5 border-b border-white/[0.05] hover:bg-white/[0.02] transition-colors group" data-testid="txn-row">
                <div className="col-span-5 flex items-center gap-3">
                  <div className="h-8 w-8 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: (cat?.color || "#A1A1AA") + "22" }}>
                    {isIncome ? <ArrowUpRight size={16} className="text-income" /> : <ArrowDownRight size={16} className="text-expense" />}
                  </div>
                  <span className="text-sm font-medium truncate">{t.description || cat?.name || "Transaction"}</span>
                </div>
                <div className="col-span-3 text-sm text-[#A1A1AA]">
                  <span className="inline-flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: cat?.color || "#A1A1AA" }} />
                    {cat?.name || "Uncategorized"}
                  </span>
                </div>
                <div className="col-span-2 text-sm text-[#52525B] num">{t.date}</div>
                <div className="col-span-2 flex items-center justify-end gap-3">
                  <span className={`num text-sm font-medium ${isIncome ? "text-income" : "text-expense"}`}>
                    {isIncome ? "+" : "-"}{fmt(t.amount, t.currency)}
                  </span>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => openEdit(t)} data-testid="edit-txn-btn" className="text-[#52525B] hover:text-cyan"><PencilSimple size={16} /></button>
                    <button onClick={() => remove(t.id)} data-testid="delete-txn-btn" className="text-[#52525B] hover:text-expense"><Trash size={16} /></button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Transaction" : "Add Transaction"}>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {["EXPENSE", "INCOME"].map((ty) => (
              <button type="button" key={ty} onClick={() => setForm((f) => ({ ...f, type: ty, categoryId: "" }))}
                data-testid={`type-${ty.toLowerCase()}`}
                className={`py-2.5 rounded-md text-sm font-semibold border transition-colors ${
                  form.type === ty
                    ? ty === "INCOME" ? "bg-income/15 text-income border-income/30" : "bg-expense/15 text-expense border-expense/30"
                    : "border-white/10 text-[#A1A1AA] hover:bg-white/[0.04]"
                }`}>
                {ty === "INCOME" ? "Income" : "Expense"}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Input label="Amount" type="number" step="0.01" min="0" value={form.amount} onChange={set("amount")} placeholder="0.00" required data-testid="txn-amount" />
            </div>
            <Select label="Currency" value={form.currency} onChange={set("currency")} data-testid="txn-currency">
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </div>
          <Select label="Category" value={form.categoryId} onChange={set("categoryId")} data-testid="txn-category">
            <option value="">Uncategorized</option>
            {filteredCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Input label="Description" value={form.description} onChange={set("description")} placeholder="e.g. Grocery shopping" data-testid="txn-description" />
          <Input label="Date" type="date" value={form.date} onChange={set("date")} required data-testid="txn-date" />
          <Button type="submit" disabled={saving} className="w-full" data-testid="save-txn-btn">
            {saving ? "Saving..." : editing ? "Update" : "Add Transaction"}
          </Button>
        </form>
      </Modal>
    </div>
  );
}
